// cascade-resolver.js — Pure logic + DOM init für die Fitment-Cascade
// Pure-Logic-Block (exportiert für Tests via node:test)

export function filterMarkenByCategory(marken, category) {
  if (category === 'any' || !category) return marken;
  return marken.filter(m => {
    // Bevorzugt: `categories` ist die tatsächliche Liste der Kategorien, die
    // diese Marke in `modell_categories` führt. Damit landen "mixed"-Marken
    // (BMW, Honda etc.) nur in Collections, in denen sie wirklich Modelle haben.
    if (Array.isArray(m.categories)) return m.categories.includes(category);
    // Legacy-Fallback für ältere Index-Files ohne `categories`. `mixed` heißt
    // historisch "a+b" — niemals "h", also bei Pferdeanhängern ausschließen.
    if (m.category === category) return true;
    if (m.category === 'mixed' && (category === 'a' || category === 'b')) return true;
    return false;
  });
}

// Marken, die je Kategorie ganz oben (vor dem Trenner) stehen sollen — die
// meistgewählten, Reihenfolge von Veronika vorgegeben. Slugs = fitment-index.json.
// Darunter folgt ein Trenner und dann die restlichen Marken alphabetisch.
export const PINNED_MARKEN = {
  a: ['audi', 'bmw', 'ford', 'mercedes-benz', 'opel', 'porsche', 'skoda', 'toyota', 'volvo', 'vw'],
  b: ['bmw', 'honda', 'yamaha', 'kawasaki', 'suzuki', 'harley-davidson', 'ducati', 'triumph'],
  h: ['boeckmann', 'humbaur', 'cheval-libert', 'ifor-williams', 'wm-meyer', 'westfalia', 'schmidt'],
};

// Sentinel-Eintrag für den Trenner zwischen gepinnten und alphabetischen Marken.
export const MARKE_DIVIDER = { divider: true };

export function reorderMarkenForDisplay(marken, category) {
  const sorted = [...marken].sort((a, b) =>
    (a.label || '').localeCompare(b.label || '', 'de', { sensitivity: 'base' }));
  const pins = PINNED_MARKEN[category];
  if (!pins || !pins.length) return sorted;
  const bySlug = new Map(sorted.map(m => [m.slug, m]));
  const pinnedSlugs = new Set();
  const pinned = [];
  for (const slug of pins) {
    const m = bySlug.get(slug);
    if (m) { pinned.push(m); pinnedSlugs.add(slug); }
  }
  if (!pinned.length) return sorted;
  const rest = sorted.filter(m => !pinnedSlugs.has(m.slug));
  if (!rest.length) return pinned;
  return [...pinned, MARKE_DIVIDER, ...rest];
}

export function getModelle(brand, category) {
  const keys = Object.keys(brand?.tree || {})
    .sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }));
  // Wenn Kategorie-Filter aktiv UND Brand-File die modell_categories hat:
  // nur Modelle der passenden Kategorie zeigen ("any" zeigt alle).
  if (!category || category === 'any' || !brand?.modell_categories) return keys;
  return keys.filter(m => brand.modell_categories[m] === category);
}

export function getBaujahre(brand, modell) {
  return Object.keys(brand?.tree?.[modell] || {});
}

export function resolveMatches(brand, modell, baujahr) {
  return brand?.tree?.[modell]?.[baujahr] || [];
}

// Shopify stellt das Locale-Präfix als window.Shopify.routes.root bereit:
// '/' auf dem deutschen Storefront, '/en/' auf dem englischen.
export function localeRoot() {
  const root = typeof window !== 'undefined' && window.Shopify?.routes?.root;
  return normalizeRoot(root);
}

function normalizeRoot(root) {
  if (!root) return '/';
  let r = String(root);
  if (!r.startsWith('/')) r = '/' + r;
  if (!r.endsWith('/')) r += '/';
  return r;
}

export function buildMultiMatchUrlPure(matches, ctx, collectionHandle = 'all', root = '/') {
  // Encoding: handle:size — pro Treffer die Größe mitschicken, damit JS auf der
  // Collection den richtigen ?size=… in den Card-Href setzen kann.
  // Bei Duplikat-Handles: bevorzuge den ersten size-Wert.
  const handleSize = new Map();
  for (const m of matches) {
    if (!handleSize.has(m.handle)) handleSize.set(m.handle, m.size || '');
  }
  const handlesEncoded = [...handleSize.entries()]
    .map(([h, s]) => s ? `${h}:${s}` : h)
    .join(',');

  // sharedSize bleibt für den Single-Match-Fallback und als globaler Hint
  const sizes = new Set([...handleSize.values()].filter(Boolean));
  const sharedSize = sizes.size === 1 ? [...sizes][0] : '';

  const params = new URLSearchParams();
  params.set('handles', handlesEncoded);
  if (sharedSize) params.set('size', sharedSize);
  if (ctx.marke) params.set('marke', ctx.marke);
  if (ctx.modell) params.set('modell', ctx.modell);
  if (ctx.baujahr) params.set('baujahr', ctx.baujahr);
  return `${normalizeRoot(root)}collections/${collectionHandle}?${params.toString()}`;
}

export function resolveBrandUrl(brandUrlOrFilename, indexUrl) {
  // Bereits absolut?
  if (/^https?:\/\//i.test(brandUrlOrFilename)) return brandUrlOrFilename;
  // Relativ zur Index-URL auflösen, ?v=… vom Index droppen
  let baseUrl = indexUrl.split('?')[0];
  // Shopify liefert protocol-relative URLs (//host/...) — new URL() braucht ein Protokoll
  if (baseUrl.startsWith('//')) baseUrl = 'https:' + baseUrl;
  return new URL(brandUrlOrFilename, baseUrl).href;
}

// ─── DOM-Init-Block ────────────────────────────────────────────────────

const CASCADE_DEBUG = true; // Bewusst hart an, solange das Live-Setup justiert wird
function clog(...args) {
  if (CASCADE_DEBUG && typeof console !== 'undefined') {
    try { console.log('[cascade]', ...args); } catch (_) {}
  }
}

const STATE = new WeakMap(); // container → { index, brand, indexUrl }

function $$(root, sel) { return Array.from(root.querySelectorAll(sel)); }
function $(root, sel)  { return root.querySelector(sel); }

function getLocale(key, fallback) {
  return (window.cecLocale && window.cecLocale[key]) || fallback;
}

function clearOptions(sel) {
  Array.from(sel.options).forEach(o => {
    if (!o.classList.contains('default') && o.value !== '') o.remove();
  });
}

function disableAndReset(sel) {
  if (!sel) return;
  sel.value = '';
  sel.disabled = true;
  delete sel.dataset.cascadeOptional;
  sel.closest('.form-group')?.classList.add('disabled');
  clearOptions(sel);
}

function enable(sel) {
  if (!sel) return;
  delete sel.dataset.cascadeOptional;
  sel.disabled = false;
  sel.closest('.form-group')?.classList.remove('disabled');
}

function setLoading(sel, on) {
  if (!sel) return;
  const group = sel.closest('.form-group');
  if (on) {
    sel.disabled = true;
    sel.setAttribute('aria-busy', 'true');
    group?.classList.add('cascade-loading');
  } else {
    sel.removeAttribute('aria-busy');
    group?.classList.remove('cascade-loading');
  }
}

function populateSelect(sel, items, labelFn = String) {
  clearOptions(sel);
  items.forEach(item => {
    if (item && item.divider) {
      const opt = document.createElement('option');
      opt.disabled = true;
      opt.className = 'cascade-divider';
      opt.textContent = '──────────';
      sel.appendChild(opt);
      return;
    }
    const value = typeof item === 'string' ? item : item.slug;
    const label = labelFn(item);
    // Skip empties — sonst landet eine blank-Zeile zwischen den echten Einträgen
    if (value == null || value === '' || label == null || label === '') return;
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = label;
    sel.appendChild(opt);
  });
}

function setNoBaujahrRequired(sel) {
  clearOptions(sel);
  const opt = document.createElement('option');
  opt.value = '';
  opt.textContent = getLocale('cascade_no_baujahr_required', 'Kein Baujahr erforderlich');
  opt.selected = true;
  sel.appendChild(opt);
  sel.disabled = true;
  sel.dataset.cascadeOptional = 'true';
  sel.closest('.form-group')?.classList.remove('disabled', 'cascade-skipped');
}

function bust(url) {
  // Cache-Bust: Shopify-CDN keyt nur auf den ?v=-Parameter. ?cb=… wird ignoriert
  // (alle ?cb-Requests teilen sich denselben Cache-Eintrag und können stale werden).
  // Wir ersetzen also IMMER das vorhandene ?v=… durch einen frischen Wert.
  const u = new URL(url, typeof window !== 'undefined' ? window.location.href : 'https://localhost/');
  u.searchParams.set('v', String(Date.now()));
  // Liefere ohne Origin zurück wenn die Quelle protocol-relativ war
  if (url.startsWith('//')) return '//' + u.host + u.pathname + (u.search || '');
  if (url.startsWith('/')) return u.pathname + (u.search || '');
  return u.href;
}

async function loadIndex(container) {
  const indexUrl = container.dataset.indexUrl;
  if (!indexUrl) throw new Error('cascade-resolver: data-index-url missing');
  const fetchUrl = bust(indexUrl);
  clog('loadIndex:', fetchUrl);
  const r = await fetch(fetchUrl, { credentials: 'omit' });
  if (!r.ok) throw new Error(`Index fetch failed: ${r.status}`);
  const data = await r.json();
  clog('loadIndex done:', { marken: data?.marken?.length, _v: data?._v });
  STATE.set(container, { index: data, indexUrl, brand: null });
  return data;
}

async function loadBrand(container, slug) {
  const state = STATE.get(container);
  if (!state) throw new Error('No state');
  const marke = state.index.marken.find(m => m.slug === slug);
  if (!marke) throw new Error(`Brand ${slug} not in index`);
  const url = resolveBrandUrl(marke.url, state.indexUrl);
  const fetchUrl = bust(url);
  clog('loadBrand:', slug, fetchUrl);
  const r = await fetch(fetchUrl, { credentials: 'omit' });
  if (!r.ok) throw new Error(`Brand fetch failed: ${r.status}`);
  state.brand = await r.json();
  state.brand._slug = slug;
  const tree = state.brand?.tree || {};
  const mc = state.brand?.modell_categories || {};
  clog('loadBrand done:', slug, {
    _v: state.brand?._v,
    modelle_total: Object.keys(tree).length,
    modell_categories_count: Object.keys(mc).length,
    has_modell_categories: !!state.brand?.modell_categories,
    erste_tree_keys: Object.keys(tree).slice(0, 5),
  });
  return state.brand;
}

function showError(container, msg) {
  let banner = container.querySelector('.cascade-error-banner');
  if (!banner) {
    banner = document.createElement('div');
    banner.className = 'cascade-error-banner';
    banner.setAttribute('role', 'alert');
    container.prepend(banner);
  }
  banner.textContent = msg;
  banner.hidden = false;
}

function hideError(container) {
  const banner = container.querySelector('.cascade-error-banner');
  if (banner) banner.hidden = true;
}

function updateSubmitState(container) {
  const submitBtn = container.querySelector('button[type="submit"]');
  if (!submitBtn) return;
  // Submit enabled wenn alle sichtbaren (nicht-cascade-skipped) Selects einen Wert haben
  const required = $$(container, 'select[data-cascade-step]').filter(s =>
    !s.closest('.form-group')?.classList.contains('cascade-skipped') &&
    s.dataset.cascadeOptional !== 'true'
  );
  const allFilled = required.length > 0 && required.every(s => s.value);
  submitBtn.disabled = !allFilled;
}

async function loadBrandAndPopulateModelle(container, slug) {
  const brandSel = $(container, 'select[data-cascade-step="marke"]');
  const modellSel = $(container, 'select[data-cascade-step="modell"]');
  const baujahrSel = $(container, 'select[data-cascade-step="baujahr"]');
  [modellSel, baujahrSel].forEach(disableAndReset);
  modellSel?.closest('.form-group')?.classList.remove('cascade-skipped');
  baujahrSel?.closest('.form-group')?.classList.remove('cascade-skipped');
  hideError(container);
  setLoading(modellSel, true);
  try {
    const brand = await loadBrand(container, slug);
    setLoading(modellSel, false);
    const cat = container.dataset.cascadeCategory || 'any';
    const modelle = getModelle(brand, cat);
    clog('loadBrandAndPopulateModelle:', { slug, category: cat, modelle_nach_filter: modelle.length, erste: modelle.slice(0, 5) });
    if (modellSel) {
      populateSelect(modellSel, modelle);
      enable(modellSel);
    }
    updateSubmitState(container);
    return brand;
  } catch (err) {
    setLoading(modellSel, false);
    brandSel.value = '';
    showError(container, getLocale('cascade_error_brand_fetch',
      'Daten temporär nicht verfügbar, bitte erneut auswählen.'));
    updateSubmitState(container);
    throw err;
  }
}

function onMarkeChange(container) {
  const brandSel = $(container, 'select[data-cascade-step="marke"]');
  const slug = brandSel.value;
  if (!slug) {
    const modellSel = $(container, 'select[data-cascade-step="modell"]');
    const baujahrSel = $(container, 'select[data-cascade-step="baujahr"]');
    [modellSel, baujahrSel].forEach(disableAndReset);
    updateSubmitState(container);
    return;
  }
  loadBrandAndPopulateModelle(container, slug).catch(err => console.error(err));
}

async function applyUrlState(container) {
  const params = new URLSearchParams(window.location.search);
  const markeParam = params.get('marke');
  const modellParam = params.get('modell');
  const baujahrParam = params.get('baujahr');
  if (!markeParam) return;

  const state = STATE.get(container);
  if (!state?.index) return;

  const lc = (s) => (s || '').toString().trim().toLowerCase();
  const matched = state.index.marken.find(m =>
    lc(m.label) === lc(markeParam) || lc(m.slug) === lc(markeParam)
  );
  if (!matched) {
    clog('applyUrlState: marke nicht in Index gefunden', markeParam);
    return;
  }

  const brandSel = $(container, 'select[data-cascade-step="marke"]');
  if (!brandSel) return;
  brandSel.value = matched.slug;
  clog('applyUrlState: marke preselected', { label: matched.label, slug: matched.slug });

  try {
    await loadBrandAndPopulateModelle(container, matched.slug);
  } catch (err) {
    clog('applyUrlState: loadBrand failed', err);
    return;
  }

  if (!modellParam) return;
  const modellSel = $(container, 'select[data-cascade-step="modell"]');
  if (!modellSel) return;
  const opts = Array.from(modellSel.options).map(o => o.value);
  if (!opts.includes(modellParam)) {
    clog('applyUrlState: modell nicht in den Optionen', modellParam, 'avail erste:', opts.slice(0, 8));
    return;
  }
  modellSel.value = modellParam;
  onModellChange(container);
  clog('applyUrlState: modell preselected', modellParam);

  if (!baujahrParam) return;
  const baujahrSel = $(container, 'select[data-cascade-step="baujahr"]');
  if (!baujahrSel) return;
  if (baujahrSel.closest('.form-group')?.classList.contains('cascade-skipped')) {
    clog('applyUrlState: baujahr skipped (Modell hat keine Baujahre)');
    return;
  }
  const bjOpts = Array.from(baujahrSel.options).map(o => o.value);
  if (!bjOpts.includes(baujahrParam)) {
    clog('applyUrlState: baujahr nicht in den Optionen', baujahrParam, 'avail erste:', bjOpts.slice(0, 8));
    return;
  }
  baujahrSel.value = baujahrParam;
  updateSubmitState(container);
  clog('applyUrlState: baujahr preselected', baujahrParam);
}

function onModellChange(container) {
  const modellSel = $(container, 'select[data-cascade-step="modell"]');
  const baujahrSel = $(container, 'select[data-cascade-step="baujahr"]');
  disableAndReset(baujahrSel);
  baujahrSel?.closest('.form-group')?.classList.remove('cascade-skipped');
  const state = STATE.get(container);
  if (!state?.brand || !modellSel?.value) {
    clog('onModellChange: no state.brand or no modell selected');
    updateSubmitState(container);
    return;
  }
  const baujahre = getBaujahre(state.brand, modellSel.value);
  clog('onModellChange:', { modell: modellSel.value, baujahre_count: baujahre.length, baujahre_sample: baujahre.slice(0, 5) });
  // Baujahr ist optional, wenn das Brand-File bewusst nur den leeren Baujahr-Key hat.
  if (!baujahrSel) {
    clog('onModellChange: kein baujahrSel');
    updateSubmitState(container);
    return;
  }
  if (baujahre.length === 0 || (baujahre.length === 1 && baujahre[0] === '')) {
    clog('onModellChange: baujahr optional/leer', { modell: modellSel.value });
    setNoBaujahrRequired(baujahrSel);
    updateSubmitState(container);
    return;
  }
  populateSelect(baujahrSel, baujahre);
  enable(baujahrSel);
  updateSubmitState(container);
}

function onBaujahrChange(container) {
  updateSubmitState(container);
}

// Auto-Suche: sobald alle Pflicht-Selects gefüllt sind, direkt suchen —
// nur nach echten User-Änderungen (change-Events), nie nach applyUrlState-Prefill,
// sonst Navigations-Loop auf der Ergebnis-Seite.
function maybeAutoSubmit(container) {
  const submitBtn = container.querySelector('button[type="submit"]');
  if (!submitBtn || submitBtn.disabled) return;
  const form = container.querySelector('form.section-filters-form, form');
  if (!form) return;
  clog('maybeAutoSubmit: alle Felder gefüllt → Suche');
  if (typeof form.requestSubmit === 'function') form.requestSubmit();
  else form.dispatchEvent(new Event('submit', { cancelable: true }));
}

export function categoryToCollection(cat) {
  // Mapping Cover-Category → Collection-Handle (live shop)
  switch (cat) {
    case 'a': return 'autoabdeckung';
    case 'b': return 'motorrad-abdeckplane';
    case 'h': return 'schutzhaube-pferdeanhanger';
    default:  return 'all';
  }
}

// Hinweis: Es gibt keinen Single-Match-Direktsprung auf /products/<handle> mehr.
// Seit dem Key-Redesign trägt match.handle einen stabilen fitment_key (keinen
// Produkt-Handle), also routen ALLE Treffer — auch genau einer — über die
// Collection. collection-handle-filter.js zeigt dort die passende(n) Karte(n)
// per data-fitment-key. Das hält die Cascade vollständig handle-frei (fail-proof).

function buildMatchUrl(matches, ctx, brand, container) {
  // → /collections/<handle>?handles=k1,k2,k3&size=...&marke=...
  // Die handles-Werte sind stabile fitment_keys (siehe collection-handle-filter.js).
  // collection-handle-filter.js versteckt Cards, deren data-fitment-key nicht in der
  // Liste ist, und rendert einen Suchergebnis-Banner. (Native Facet-Filter scheidet
  // aus: Produkte haben die custom.marke/reihe/baujahr-Metafields nicht gesetzt.)
  // Locale-Root mitgeben, sonst wirft der Submit den Kunden von /en/… zurück in den
  // deutschen Shop — und die Same-Path-Optimierung in onSubmit greift nie.
  const collectionHandle = categoryToCollection(container?.dataset?.cascadeCategory);
  return buildMultiMatchUrlPure(matches, ctx, collectionHandle, localeRoot());
}

function onSubmit(container, evt) {
  evt.preventDefault();
  const state = STATE.get(container);
  if (!state?.brand) return;
  const brandSel = $(container, 'select[data-cascade-step="marke"]');
  const modellSel = $(container, 'select[data-cascade-step="modell"]');
  const baujahrSel = $(container, 'select[data-cascade-step="baujahr"]');

  if (!modellSel?.value) return;
  const modell = modellSel.value;

  // Baujahr-Skip: bei leerem Tree-Baujahr-Key gilt baujahr=""
  const baujahrSkipped = !baujahrSel ||
    baujahrSel.closest('.form-group')?.classList.contains('cascade-skipped') ||
    baujahrSel.dataset.cascadeOptional === 'true';
  const baujahr = baujahrSkipped ? '' : baujahrSel.value;
  if (!baujahrSkipped && !baujahr) return;

  const matches = resolveMatches(state.brand, modell, baujahr);
  const ctx = {
    marke: brandSel.options[brandSel.selectedIndex]?.textContent || brandSel.value,
    modell,
    baujahr,
  };

  if (matches.length === 0) {
    showError(container, getLocale('cascade_error_no_match', 'Keine passende Abdeckung gefunden.'));
    return;
  }
  // Ein oder mehrere Treffer → immer über die Collection (handle-frei).
  const target = buildMatchUrl(matches, ctx, state.brand, container);

  // Same-path-Optimierung: Wenn das Ziel auf der gleichen Collection liegt, auf
  // der wir schon sind, nicht reloaden — pushState + Card-Filter neu anwenden.
  // Vermeidet den Page-Jump bei Suchverfeinerung auf derselben Seite.
  try {
    const tu = new URL(target, window.location.origin);
    if (tu.pathname === window.location.pathname) {
      history.pushState({}, '', tu.toString());
      if (typeof window.__cecApplyHandleFilter === 'function') {
        window.__cecApplyHandleFilter();
      }
      return;
    }
  } catch (_) { /* fallback to full nav */ }

  window.location.href = target;
}

function initContainer(container) {
  const brandSel = $(container, 'select[data-cascade-step="marke"]');
  const modellSel = $(container, 'select[data-cascade-step="modell"]');
  const baujahrSel = $(container, 'select[data-cascade-step="baujahr"]');
  [modellSel, baujahrSel].forEach(disableAndReset);

  if (!brandSel) return;
  brandSel.addEventListener('change', () => onMarkeChange(container));
  modellSel?.addEventListener('change', () => {
    onModellChange(container);
    maybeAutoSubmit(container);
  });
  baujahrSel?.addEventListener('change', () => {
    onBaujahrChange(container);
    maybeAutoSubmit(container);
  });
  const form = container.querySelector('form.section-filters-form, form');
  form?.addEventListener('submit', evt => onSubmit(container, evt));
  // Initial Submit-Button auf disabled
  updateSubmitState(container);
  setLoading(brandSel, true);
  loadIndex(container)
    .then(async idx => {
      const cat = container.dataset.cascadeCategory || 'any';
      const marken = reorderMarkenForDisplay(filterMarkenByCategory(idx.marken, cat), cat);
      populateSelect(brandSel, marken, m => m.label);
      setLoading(brandSel, false);
      enable(brandSel);
      updateSubmitState(container);
      // Wenn die URL bereits Cascade-Werte trägt (z.B. nach Suche-Submit landet User wieder auf
      // /collections/<handle>?marke=…&modell=…&baujahr=…): Selects vorbelegen.
      try { await applyUrlState(container); } catch (e) { clog('applyUrlState error', e); }
    })
    .catch(err => {
      console.error(err);
      setLoading(brandSel, false);
      brandSel.disabled = true;
      brandSel.closest('.form-group')?.classList.add('cascade-error');
    });
}

if (typeof document !== 'undefined') {
  const bootCascade = () =>
    document.querySelectorAll('[data-cascade-resolver]').forEach(initContainer);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCascade);
  } else {
    bootCascade();
  }
}
